import { z } from 'zod'
import { contentTypeSchema } from './common'
import type { PaginatedResponse } from './common'

const baseContentItemSchema = z.object({
  title_en: z.string().min(3).max(200),
  title_hi: z.string().min(3).max(200),
  body_en: z.string().max(10000).optional(),
  body_hi: z.string().max(10000).optional(),
  media_url: z.string().url().optional(),
  category_id: z.string().min(1),
  type: contentTypeSchema,
  is_published: z.boolean().default(false),
  published_at: z.string().datetime().optional(),
  tier_required: z.enum(['free', 'premium']).nullable().optional(),
  tier_level_required: z.number().int().min(0).max(1).default(0),
  is_free: z.boolean().default(false),
  thumbnail_url: z.string().url().optional(),
})

function validateContentFieldsCreate(
  value: z.infer<typeof baseContentItemSchema>,
  ctx: z.RefinementCtx,
) {
  if ((value.type === 'article' || value.type === 'quote') && !value.body_en) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'body_en is required for article/quote',
      path: ['body_en'],
    })
  }
  if ((value.type === 'article' || value.type === 'quote') && !value.body_hi) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'body_hi is required for article/quote',
      path: ['body_hi'],
    })
  }
  if (
    (value.type === 'youtube' || value.type === 'ebook') &&
    !value.media_url
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'media_url is required for youtube/ebook',
      path: ['media_url'],
    })
  }
}

const updateContentItemBaseSchema = baseContentItemSchema.partial()

function validateContentFieldsUpdate(
  value: z.infer<typeof updateContentItemBaseSchema>,
  ctx: z.RefinementCtx,
) {
  if (!value.type) return
  if ((value.type === 'article' || value.type === 'quote') && !value.body_en) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'body_en is required for article/quote',
      path: ['body_en'],
    })
  }
  if ((value.type === 'article' || value.type === 'quote') && !value.body_hi) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'body_hi is required for article/quote',
      path: ['body_hi'],
    })
  }
  if (
    (value.type === 'youtube' || value.type === 'ebook') &&
    !value.media_url
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'media_url is required for youtube/ebook',
      path: ['media_url'],
    })
  }
}

export const createContentItemSchema = baseContentItemSchema.superRefine(
  validateContentFieldsCreate,
)
export const updateContentItemSchema = updateContentItemBaseSchema.superRefine(
  validateContentFieldsUpdate,
)

export type CreateContentItemInput = z.infer<typeof createContentItemSchema>
export type UpdateContentItemInput = z.infer<typeof updateContentItemSchema>

export type ContentItemResponse = {
  id: string
  title_en: string
  title_hi: string
  body_en?: string | null
  body_hi?: string | null
  media_url?: string | null
  category_id: string
  type: z.infer<typeof contentTypeSchema>
  is_published: boolean
  published_at?: string | null
  tier_required?: string | null
  tier_level_required: number
  is_free: boolean
  thumbnail_url?: string | null
  created_at: string
  updated_at: string
}

export type PublicContentItemResponse = {
  id: string
  title: string
  body?: string | null
  media_url?: string | null
  type: z.infer<typeof contentTypeSchema>
  category_id: string
  is_published: boolean
  published_at?: string | null
  thumbnail_url?: string | null
  tier_required?: string | null
  tier_level_required?: number
  is_free?: boolean
  locked?: boolean
  unlock_message?: string | null
}

export type ContentSortBy =
  | 'created_at'
  | 'published_at'
  | 'title_en'
  | 'title_hi'

export type PaginatedContentItemResponse =
  PaginatedResponse<ContentItemResponse>
export type PaginatedPublicContentItemResponse =
  PaginatedResponse<PublicContentItemResponse>
